import { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Download, RefreshCcw, Mail, Phone, Building2, Check, MessageSquare, Search, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const nav = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem("who_admin_token") || "");
  const [contacts, setContacts] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("contacts"); // contacts | guide
  const [query, setQuery] = useState("");
  const [showContactedOnly, setShowContactedOnly] = useState(false);
  const [showPilotOnly, setShowPilotOnly] = useState(false);

  useEffect(() => {
    if (!token) {
      nav("/admin/login", { replace: true });
      return;
    }
    load();
  }, [token]);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        axios.get(`${API}/admin/contacts`, authHeaders),
        axios.get(`${API}/admin/guide-interactions`, authHeaders),
      ]);
      setContacts(c.data.submissions || []);
      setInteractions(g.data.interactions || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("who_admin_token");
        setToken("");
        nav("/admin/login", { replace: true });
        return;
      }
      toast.error("Failed to load. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("who_admin_token");
    setToken("");
    nav("/admin/login", { replace: true });
  };

  const toggleContacted = async (row) => {
    const next = !row.contacted;
    // optimistic
    setContacts((cs) => cs.map((c) => (c.id === row.id ? { ...c, contacted: next } : c)));
    try {
      await axios.patch(`${API}/admin/contacts/${row.id}`, { contacted: next }, authHeaders);
    } catch (_) {
      toast.error("Update failed");
      setContacts((cs) => cs.map((c) => (c.id === row.id ? { ...c, contacted: !next } : c)));
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return contacts.filter((r) => {
      if (showContactedOnly && !r.contacted) return false;
      if (showPilotOnly && !r.pilot_interest) return false;
      if (!q) return true;
      return (
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.organization || "").toLowerCase().includes(q) ||
        (r.organization_type || "").toLowerCase().includes(q) ||
        (r.message || "").toLowerCase().includes(q)
      );
    });
  }, [contacts, query, showContactedOnly, showPilotOnly]);

  const exportCsv = () => {
    const cols = ["name", "email", "organization", "title", "phone", "organization_type", "pilot_interest", "contacted", "submitted_at", "message"];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const rows = [cols.join(",")].concat(filtered.map((r) => cols.map((c) => escape(r[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `who-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const stats = useMemo(() => ({
    total: contacts.length,
    pilot: contacts.filter((c) => c.pilot_interest).length,
    contacted: contacts.filter((c) => c.contacted).length,
    guideInteractions: interactions.length,
  }), [contacts, interactions]);

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard-page">
      {/* Header */}
      <header className="bg-navy-deep text-cream border-b border-white/10">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-cream font-display font-black text-xl tracking-tight">WHO · Admin</Link>
            <span className="hidden md:inline text-cream/40">·</span>
            <span className="hidden md:inline text-cream/60 text-sm">Relationship Operating System · Ops</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cream/70 hover:text-gold transition-colors"
            >
              <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cream/70 hover:text-red-300 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-10">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Total inquiries" value={stats.total} testId="admin-stat-total" />
          <Stat label="Pilot interest" value={stats.pilot} testId="admin-stat-pilot" />
          <Stat label="Marked contacted" value={stats.contacted} testId="admin-stat-contacted" />
          <Stat label="WHO Guides sessions" value={stats.guideInteractions} testId="admin-stat-guide" />
        </div>

        {/* Tabs */}
        <div className="mt-10 border-b border-borderSubtle flex items-center gap-8">
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testId="admin-tab-contacts">Contact Submissions</TabBtn>
          <TabBtn active={tab === "guide"} onClick={() => setTab("guide")} testId="admin-tab-guide">WHO Guides Log</TabBtn>
        </div>

        {tab === "contacts" && (
          <div className="mt-8">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slateSoft" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search name, email, org, message…"
                  data-testid="admin-search-input"
                  className="w-full pl-9 pr-3 py-2.5 rounded-md bg-white border border-borderSubtle focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40 text-navy text-sm"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid="admin-filter-pilot">
                <input type="checkbox" checked={showPilotOnly} onChange={(e) => setShowPilotOnly(e.target.checked)} className="accent-gold" />
                Pilot only
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-navy cursor-pointer" data-testid="admin-filter-contacted">
                <input type="checkbox" checked={showContactedOnly} onChange={(e) => setShowContactedOnly(e.target.checked)} className="accent-gold" />
                Contacted only
              </label>
              <button
                type="button"
                onClick={exportCsv}
                data-testid="admin-export-csv-btn"
                className="ml-auto inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-cream text-sm font-semibold px-4 py-2.5 rounded-md transition-colors"
              >
                <Download className="w-4 h-4" /> Export CSV
              </button>
            </div>

            {/* Contact list */}
            {loading ? (
              <p className="text-slateSoft" data-testid="admin-loading">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="p-10 bg-white border border-borderSubtle rounded-lg text-center" data-testid="admin-empty">
                <MessageSquare className="w-8 h-8 text-slateSoft mx-auto opacity-60" />
                <p className="mt-3 text-navy font-semibold">No matches.</p>
                <p className="text-sm text-slateSoft mt-1">Adjust filters or clear the search.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-contact-list">
                {filtered.map((row) => (
                  <ContactRow key={row.id} row={row} onToggle={() => toggleContacted(row)} />
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "guide" && (
          <div className="mt-8 space-y-3" data-testid="admin-guide-list">
            {loading ? (
              <p className="text-slateSoft">Loading…</p>
            ) : interactions.length === 0 ? (
              <div className="p-10 bg-white border border-borderSubtle rounded-lg text-center">
                <p className="text-navy font-semibold">No WHO Guides sessions yet.</p>
                <p className="text-sm text-slateSoft mt-1">Try the live demo on the homepage.</p>
              </div>
            ) : (
              interactions.map((row, i) => (
                <div key={row.id || i} className="p-6 bg-white border border-borderSubtle rounded-lg" data-testid={`admin-guide-row-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-slateSoft">Session {(row.session_id || "").slice(0, 8)} · {row.created_at?.slice(0, 19).replace("T", " ")}</p>
                  </div>
                  <p className="text-navy"><span className="font-semibold text-gold-deep">You:</span> {row.user_transcript}</p>
                  <p className="mt-2 text-navy"><span className="font-semibold text-gold-deep">Maya:</span> {row.guide_reply}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="p-5 bg-white border border-borderSubtle rounded-lg" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-widest text-slateSoft font-semibold">{label}</p>
      <p className="mt-2 font-display font-black text-navy text-3xl">{value}</p>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`pb-3 -mb-px border-b-2 font-display font-bold text-lg transition-colors ${
        active ? "border-gold text-navy" : "border-transparent text-slateSoft hover:text-navy"
      }`}
    >
      {children}
    </button>
  );
}

function ContactRow({ row, onToggle }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white border border-borderSubtle rounded-lg overflow-hidden" data-testid={`admin-contact-row-${row.id}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex flex-wrap items-center gap-3 md:gap-6 p-5 hover:bg-cream-panel/40 transition-colors text-left"
      >
        <div className="flex-1 min-w-[180px]">
          <p className="font-display font-bold text-navy">{row.name}</p>
          <p className="text-xs text-slateSoft flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" /> {row.email}</p>
        </div>
        <div className="hidden md:block flex-1 min-w-[180px]">
          {row.organization && <p className="text-navy text-sm font-medium flex items-center gap-1"><Building2 className="w-3 h-3 text-slateSoft" /> {row.organization}</p>}
          {row.organization_type && <p className="text-xs text-slateSoft mt-0.5">{row.organization_type}</p>}
        </div>
        <div className="hidden md:flex flex-col items-end min-w-[110px]">
          <p className="text-[10px] uppercase tracking-widest text-slateSoft">
            {row.submitted_at?.slice(0, 10)}
          </p>
          {row.pilot_interest && (
            <span className="mt-1 text-[10px] font-bold uppercase tracking-widest bg-gold/15 text-gold-deep px-2 py-0.5 rounded">Pilot</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            data-testid={`admin-toggle-contacted-${row.id}`}
            className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${row.contacted ? "bg-gold text-navy" : "bg-navy-50 text-slateSoft hover:bg-navy-100"}`}
          >
            {row.contacted ? <><Check className="w-3 h-3" /> Contacted</> : "Mark contacted"}
          </span>
          <ChevronDown className={`w-4 h-4 text-slateSoft transition-transform ${expanded ? "rotate-180" : ""}`} />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-borderSubtle bg-cream-panel/30 p-5 grid md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slateSoft font-semibold">Details</p>
            <ul className="mt-2 space-y-1 text-navy">
              {row.title && <li>Title: {row.title}</li>}
              {row.phone && <li className="flex items-center gap-1"><Phone className="w-3 h-3" /> {row.phone}</li>}
              <li>Submitted: {row.submitted_at?.slice(0, 19).replace("T", " ")}</li>
            </ul>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slateSoft font-semibold">Message</p>
            <p className="mt-2 text-navy leading-relaxed whitespace-pre-wrap">{row.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}
